// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  // Other bodies (e.g. a File for the admin image upload) are sent as they are.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : body ? { body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Update transaction metadata related to messaging.
//
// See `server/api/update-transaction-message-metadata.js` for accepted body values.
export const updateTransactionMessageMetadata = body => {
  return post('/api/update-transaction-message-metadata', body);
};

export const googleCalendarConnectUrl = returnPath => {
  const path = '/api/google-calendar/connect';
  if (!returnPath) {
    return `${apiBaseUrl()}${path}`;
  }
  const query = new URLSearchParams({ returnPath });
  return `${apiBaseUrl()}${path}?${query.toString()}`;
};

export const disconnectGoogleCalendar = body => {
  return post('/api/google-calendar/disconnect', body || {});
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// Ask the backend to reconcile buffer availability exceptions for a
// listing. The backend will recompute the desired set of `seats=0`
// exceptions from the listing's current booking rules and its accepted
// bookings, and create/delete exceptions as needed.
export const reconcileBookingRulesBuffer = body => {
  return post('/api/reconcile-booking-rules-buffer', body);
};

// Simple JSON POST for endpoints that don't speak Transit (the onboarding
// endpoints store data in our own MongoDB rather than going through the
// Sharetribe SDK).
const postJson = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  return window
    .fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    .then(res =>
      res.json().then(data => {
        if (!res.ok) {
          const error = new Error(data?.error || data?.message || 'Request failed');
          error.status = res.status;
          error.data = data;
          throw error;
        }
        return data;
      })
    );
};

// Submit a new provider application from the public /apply page.
export const submitApplication = body => postJson('/api/applications', body);

// Presence ping — fire-and-forget. The server bumps the current user's
// profile.metadata.lastSeenOnline so other clients can render an "Online" badge.
// Returns a Promise that resolves on any non-error response (server returns 204).
export const presencePing = () => {
  const url = `${apiBaseUrl()}/api/presence/ping`;
  return window
    .fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
    .catch(() => {
      // Swallow network errors — presence is best-effort, and we don't want to surface noise.
    });
};

// Simple JSON GET for the admin onboarding endpoints.
const getJson = path => {
  const url = `${apiBaseUrl()}${path}`;
  return window.fetch(url, { method: 'GET', credentials: 'include' }).then(res =>
    res.json().then(data => {
      if (!res.ok) {
        const error = new Error(data?.error || data?.message || 'Request failed');
        error.status = res.status;
        error.data = data;
        throw error;
      }
      return data;
    })
  );
};

// Admin: lists for the Onboarding dashboard. All three are gated by `isAdmin: true`
// in Sharetribe user metadata on the server. The server-side endpoints always include
// pagination meta (`{page, perPage, totalItems, totalPages}`) in the response.
const buildAdminListQuery = ({ status, page, perPage } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (page) params.set('page', String(page));
  if (perPage) params.set('perPage', String(perPage));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export const fetchAdminApplications = (options = {}) =>
  getJson(`/api/admin/applications${buildAdminListQuery(options)}`);

export const fetchAdminInvitations = (options = {}) =>
  getJson(`/api/admin/invitations${buildAdminListQuery(options)}`);

export const fetchAdminPendingListings = (options = {}) =>
  getJson(`/api/admin/pending-listings${buildAdminListQuery(options)}`);

export const approveApplication = applicationId =>
  postJson(`/api/admin/applications/${encodeURIComponent(applicationId)}/approve`, {});

export const rejectApplication = (applicationId, reason) =>
  postJson(`/api/admin/applications/${encodeURIComponent(applicationId)}/reject`, {
    reason: reason || null,
  });

// Re-send the claim email for an approved (but not yet claimed) application.
// Issues a fresh magic link server-side — the previous link stops working.
export const resendApplicationClaimEmail = applicationId =>
  postJson(`/api/admin/applications/${encodeURIComponent(applicationId)}/resend`, {});

export const createInvitation = body => postJson('/api/admin/invitations', body);

// Re-send the invitation email for a pending invitation.
// Issues a fresh magic link server-side — the previous link stops working.
export const resendInvitation = invitationId =>
  postJson(`/api/admin/invitations/${encodeURIComponent(invitationId)}/resend`, {});

export const approvePendingListing = listingId =>
  postJson(`/api/admin/listings/${encodeURIComponent(listingId)}/approve`, {});

export const rejectPendingListing = (listingId, reason) =>
  postJson(`/api/admin/listings/${encodeURIComponent(listingId)}/reject`, {
    reason: reason || null,
  });

// Simple JSON PATCH/DELETE, matching the postJson/getJson conventions.
const patchJson = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  return window
    .fetch(url, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    .then(res =>
      res.json().then(data => {
        if (!res.ok) {
          const error = new Error(data?.error || data?.message || 'Request failed');
          error.status = res.status;
          error.data = data;
          throw error;
        }
        return data;
      })
    );
};

const deleteJson = path => {
  const url = `${apiBaseUrl()}${path}`;
  return window.fetch(url, { method: 'DELETE', credentials: 'include' }).then(res =>
    res.json().then(data => {
      if (!res.ok) {
        const error = new Error(data?.error || data?.message || 'Request failed');
        error.status = res.status;
        error.data = data;
        throw error;
      }
      return data;
    })
  );
};

// Admin custom reviews (Reviews dashboard). All gated by `isAdmin: true` on the server.
export const fetchAdminExperts = () => getJson('/api/admin/experts');

export const fetchAdminExpertReviews = expertId =>
  getJson(`/api/admin/experts/${encodeURIComponent(expertId)}/reviews`);

export const createAdminCustomReview = body => postJson('/api/admin/custom-reviews', body);

export const updateAdminCustomReview = (reviewId, body) =>
  patchJson(`/api/admin/custom-reviews/${encodeURIComponent(reviewId)}`, body);

export const deleteAdminCustomReview = reviewId =>
  deleteJson(`/api/admin/custom-reviews/${encodeURIComponent(reviewId)}`);

// Recompute the rating aggregates on all of an expert's listings from ground
// truth (public Sharetribe reviews + custom reviews). Heals legacy drift and
// seeds listings published after custom reviews already existed.
export const resyncExpertRatings = expertId =>
  postJson(`/api/admin/experts/${encodeURIComponent(expertId)}/resync-ratings`, {});

// Admin curated search ranking ("Listings" dashboard tab). Rank/hide state is
// listing metadata — experts are not notified and see no change on their own
// pages; only the public search order is affected.
export const fetchAdminRankedListings = () => getJson('/api/admin/listings-ranked');

export const updateAdminListingRank = (listingId, position) =>
  postJson(`/api/admin/listings/${encodeURIComponent(listingId)}/rank`, { position });

export const updateAdminListingHidden = (listingId, hidden) =>
  postJson(`/api/admin/listings/${encodeURIComponent(listingId)}/hidden`, { hidden });

// Query string for the admin listing editor's GET endpoints: arrays are comma-joined
// (the SDK convention the server expects) and dates are sent as ISO strings.
const toQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value == null) {
      return;
    }
    const stringValue = Array.isArray(value)
      ? value.join(',')
      : value instanceof Date
      ? value.toISOString()
      : String(value);
    searchParams.set(key, stringValue);
  });
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
};

// Admin listing editor (AdminEditListingPage). These endpoints wrap the Integration
// API so an admin can edit any expert's listing and profile with the same wizard the
// experts use. They speak Transit like the SDK, so UUID/Money/LatLng/Date values
// survive the round trip and responses look exactly like SDK responses.
export const adminShowListing = (listingId, queryParams) =>
  request(`/api/admin/listings/${encodeURIComponent(listingId)}${toQueryString(queryParams)}`, {
    method: methods.GET,
  });

export const adminUpdateListing = (listingId, listing, queryParams) =>
  post(`/api/admin/listings/${encodeURIComponent(listingId)}/update`, { listing, queryParams });

export const adminCloseListing = (listingId, queryParams) =>
  post(`/api/admin/listings/${encodeURIComponent(listingId)}/close`, { queryParams });

export const adminOpenListing = (listingId, queryParams) =>
  post(`/api/admin/listings/${encodeURIComponent(listingId)}/open`, { queryParams });

export const adminQueryAvailabilityExceptions = (listingId, params) =>
  request(
    `/api/admin/listings/${encodeURIComponent(listingId)}/availability-exceptions${toQueryString(
      params
    )}`,
    { method: methods.GET }
  );

export const adminCreateAvailabilityException = (listingId, body) =>
  post(`/api/admin/listings/${encodeURIComponent(listingId)}/availability-exceptions`, body);

export const adminDeleteAvailabilityException = exceptionId =>
  request(`/api/admin/availability-exceptions/${encodeURIComponent(exceptionId)}`, {
    method: methods.DELETE,
  });

// Uploads the raw file; the response is the same image resource `sdk.images.upload` returns.
export const adminUploadImage = (file, queryParams) =>
  request(`/api/admin/images${toQueryString(queryParams)}`, {
    method: methods.POST,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });

export const adminUpdateUserProfile = (userId, profile) =>
  post(`/api/admin/users/${encodeURIComponent(userId)}/profile`, { profile });

// Public: admin-authored reviews for an expert, merged with Sharetribe reviews
// on the profile and listing pages.
//
// Unlike the other helpers this one also runs on the server (page loadData is
// executed during SSR), so it avoids `window` and falls back to the global
// fetch with the marketplace root URL as base.
export const fetchUserReviews = ({ userId, listingId }, marketplaceRootURL) => {
  const params = new URLSearchParams();
  if (userId) {
    params.set('userId', userId);
  } else if (listingId) {
    params.set('listingId', listingId);
  }
  const hasWindow = typeof window !== 'undefined';
  const baseUrl = hasWindow ? apiBaseUrl() : apiBaseUrl(marketplaceRootURL);
  const fetchFn = hasWindow ? window.fetch.bind(window) : fetch;
  return fetchFn(`${baseUrl}/api/user-reviews?${params.toString()}`, { method: 'GET' }).then(
    res => {
      if (!res.ok) {
        const error = new Error('Request failed');
        error.status = res.status;
        throw error;
      }
      return res.json();
    }
  );
};

// Magic-link claim flow — called by /onboarding/claim/:token frontend route.
export const consumeOnboardingToken = token => postJson('/api/onboarding/consume', { token });

// Called from the welcome page after the user picks their own password. Closes the
// custody window by changing the password from the server-held random one to the
// user's choice and deletes the encrypted custody copy from Mongo.
export const finalizeOnboardingPassword = newPassword =>
  postJson('/api/onboarding/finalize-password', { newPassword });

// Called from EditListingPage right after the listing draft is published. The server
// checks whether the user came through the application/invitation flow (carries
// `protectedData.onboardingPreApproved`) and approves the listing immediately if so —
// avoiding the up-to-20s wait for the backstop cron. No-op for every other user, so
// it's safe to call unconditionally.
export const maybeApproveOnboardingListing = listingId =>
  postJson('/api/onboarding/maybe-approve-listing', { listingId });
